import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Globe, StickyNote, Users, ArrowRight, ChevronDown } from 'lucide-react';
import SEOHead from './SEOHead';
import { COMPARISONS, PLATFORMS } from './seo/links';
import { FAQS, faqSchema, softwareAppSchema, organizationSchema } from './seo/faqs';
// import DemoLayer from './demo/DemoLayer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/* ═══════════════════════════════════════════
   UTILITIES
   ═══════════════════════════════════════════ */

const useInView = (opts = {}) => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setV(true); o.disconnect(); }
    }, { threshold: 0.1, ...opts });
    o.observe(el);
    return () => o.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, v];
};

// When the page was served from a pre-rendered snapshot (see scripts/apply-prerender.mjs),
// the content is already visible before React mounts. Skipping the entrance fade on that first
// paint avoids a visible blink when React swaps in its own DOM. App.jsx clears the flag after
// mount, so later SPA navigations animate normally.
const wasPrerendered = () => typeof window !== 'undefined' && window.__caydaPrerendered === true;

const Reveal = ({ children, className = '', delay = 0, y = 16 }) => {
  const [ref, v] = useInView();
  const [instant] = useState(wasPrerendered);
  const shown = v || instant;
  return (
    <div ref={ref} className={className} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? 'translateY(0)' : `translateY(${y}px)`,
      transition: instant ? 'none' : `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
    }}>{children}</div>
  );
};

/* ═══════════════════════════════════════════
   INVISIBLE CARD (disappear/reappear)
   ═══════════════════════════════════════════ */

const InvisibleCard = ({ icon, title, desc }) => {
  const [ref, inView] = useInView();
  const [phase, setPhase] = useState('glass'); // glass → hidden → accent
  const hasPlayed = useRef(false);

  useEffect(() => {
    if (!inView || hasPlayed.current) return;
    hasPlayed.current = true;
    // Start as glass, disappear, then resolve to accent
    const t1 = setTimeout(() => setPhase('hidden'), 1200);
    const t2 = setTimeout(() => setPhase('accent'), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [inView]);


  const isHidden = phase === 'hidden';
  const isAccent = phase === 'accent';

  return (
    <div ref={ref} className="relative h-full">
      <div
        className={`
          group p-5 rounded-2xl border h-full
          ${isHidden ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
          ${isAccent
            ? 'bg-accent text-white border-accent shadow-lg shadow-accent/10'
            : 'bg-white/40 backdrop-blur-md border-ink-faint/30'
          }
        `}
        style={{ transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1), background-color 0.8s cubic-bezier(0.16,1,0.3,1), border-color 0.8s cubic-bezier(0.16,1,0.3,1), box-shadow 0.8s cubic-bezier(0.16,1,0.3,1)' }}
      >
        <div className={`mb-3 transition-colors duration-800 ${isAccent ? 'text-white/80' : 'text-accent'}`}>{icon}</div>
        <h3 className={`text-sm font-display font-bold mb-1.5 transition-colors duration-800 ${isAccent ? 'text-white' : 'text-ink'}`}>{title}</h3>
        <p className={`text-[13px] leading-relaxed font-display transition-colors duration-800 ${isAccent ? 'text-white/75' : 'text-ink-secondary'}`}>{desc}</p>
      </div>
      {/* "Invisible to visitors" message */}
      <div
        className={`
          absolute inset-0 flex items-center justify-center
          transition-opacity duration-500
          ${isHidden ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <p className="text-sm font-serif italic text-ink-muted">Invisible to visitors.</p>
      </div>
    </div>
  );
};



/* ═══════════════════════════════════════════
   INTERACTIVE DEMO — Vercel screenshot
   ═══════════════════════════════════════════ */

const DEMO_NOTES = [
  { id: 1, x: 28, y: 25, author: 'Travis H.', initials: 'TH', color: 'bg-rose-500', avatar: '/demos/travis.webp', time: '2m ago', text: 'This headline is too light on mobile \u2014 bumping to semibold should fix it.', reply: null, status: 'open' },
  { id: 2, x: 72, y: 45, author: 'Holly G.', initials: 'HG', color: 'bg-sky-500', avatar: '/demos/holly.webp', time: '8m ago', text: 'The code block overflows on smaller viewports. Needs horizontal scroll or wrapping.', reply: { author: 'Travis H.', initials: 'TH', avatar: '/demos/travis.webp', text: 'Nice catch \u2014 might be the overflow-hidden on the parent.' }, status: 'in_progress' },
  { id: 3, x: 35, y: 78, author: 'Mike R.', initials: 'MR', color: 'bg-emerald-500', time: '14m ago', text: 'CTA copy updated to \u201cStart Deploying\u201d \u2014 confirmed with marketing.', reply: null, status: 'resolved' },
];

const InteractiveDemo = () => {
  const [phase, setPhase] = useState(0);
  const [activeNote, setActiveNote] = useState(null);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const t = [];
    t.push(setTimeout(() => setPhase(1), 500));
    t.push(setTimeout(() => setPhase(2), 900));
    t.push(setTimeout(() => setPhase(3), 1300));
    t.push(setTimeout(() => setActiveNote(1), 2000));
    return () => t.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (hovering || phase < 3) return;
    const i = setInterval(() => setActiveNote(p => (!p || p >= 3) ? 1 : p + 1), 4000);
    return () => clearInterval(i);
  }, [hovering, phase]);

  return (
    <div
      className="relative select-none"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); setActiveNote(null); }}
    >
      {/* Browser frame — overflow-hidden only clips the image + chrome corners */}
      <div className="rounded-xl sm:rounded-2xl overflow-hidden border border-ink-faint/40 shadow-elevated bg-white">
        <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 border-b border-ink-faint/30 bg-[#FAFAF9]">
          <div className="flex gap-1 sm:gap-1.5">
            <div className="w-2 h-2 sm:w-[10px] sm:h-[10px] rounded-full bg-[#FF5F57]" />
            <div className="w-2 h-2 sm:w-[10px] sm:h-[10px] rounded-full bg-[#FEBC2E]" />
            <div className="w-2 h-2 sm:w-[10px] sm:h-[10px] rounded-full bg-[#28C840]" />
          </div>
          <div className="flex-1 mx-3 sm:mx-6">
            <div className="bg-white border border-ink-faint/40 rounded-md sm:rounded-lg px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-[11px] text-ink-muted font-mono max-w-[160px] sm:max-w-[200px] mx-auto text-center flex items-center justify-center gap-1 sm:gap-1.5">
              <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-emerald-500" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a8 8 0 110 16A8 8 0 018 0zm3.28 5.22a.75.75 0 00-1.06 0L7 8.44 5.78 7.22a.75.75 0 00-1.06 1.06l1.75 1.75a.75.75 0 001.06 0l3.75-3.75a.75.75 0 000-1.06z"/></svg>
              vercel.com
            </div>
          </div>
        </div>

        {/* Screenshot */}
        <div className="relative">
          <img
            src="/demos/vercel.webp"
            alt="Vercel website with Cayda annotations"
            className="w-full block"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            style={{ maxHeight: 340, objectFit: 'cover', objectPosition: 'top' }}
          />
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white/60 to-transparent pointer-events-none" />
        </div>
      </div>

      {/* Annotation layer — OUTSIDE overflow-hidden so cards break out */}
      <div className="absolute inset-0 pointer-events-none" style={{ top: 30 }}>
        {DEMO_NOTES.slice(0, phase).map((note, i) => {
          const active = activeNote === note.id;
          const isResolved = note.status === 'resolved';
          return (
            <div
              key={note.id}
              className="absolute pointer-events-auto"
              style={{
                left: `${note.x}%`,
                top: `${note.y}%`,
                zIndex: active ? 40 : 20,
                animation: `pinIn 0.5s cubic-bezier(0.34,1.56,0.64,1) ${i * 150}ms backwards`,
              }}
            >
              <button
                onClick={() => setActiveNote(active ? null : note.id)}
                onMouseEnter={() => { setHovering(true); setActiveNote(note.id); }}
                className="relative group"
              >
                <div className={`
                  w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center
                  text-[9px] sm:text-[11px] font-bold text-white
                  ${isResolved ? 'bg-emerald-500 shadow-lg shadow-emerald-500/25' : 'bg-accent shadow-lg shadow-accent/25'}
                  border-2 sm:border-[2.5px] border-white
                  cursor-pointer transition-all duration-200
                  ${active ? 'scale-110' : 'group-hover:scale-110'}
                `}>
                  {isResolved ? (
                    <svg className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : note.id}
                </div>
                {note.status === 'open' && (
                  <div className="absolute inset-0 rounded-full bg-accent animate-ripple" />
                )}
              </button>

              {/* Comment card */}
              {active && (
                <div
                  className={`absolute ${note.x > 50 ? 'right-6 sm:right-9 left-auto' : 'left-6 sm:left-9'} -top-1 sm:-top-2 w-36 sm:w-60 bg-white/95 backdrop-blur-xl border border-ink-faint/30 rounded-lg sm:rounded-xl shadow-elevated z-40`}
                  style={{ animation: 'scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)' }}
                >
                  <div className="p-1.5 sm:p-3">
                    <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                      {note.avatar ? (
                        <img src={note.avatar} alt={note.author} className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full object-cover" />
                      ) : (
                        <div className={`w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full ${note.color} flex items-center justify-center text-[6px] sm:text-[8px] font-bold text-white`}>
                          {note.initials}
                        </div>
                      )}
                      <span className="text-[9px] sm:text-xs font-semibold text-ink">{note.author}</span>
                      <span className="text-[7px] sm:text-[10px] text-ink-muted ml-auto">{note.time}</span>
                    </div>
                    <p className="text-[9px] sm:text-[11px] text-ink-secondary leading-relaxed mb-1 sm:mb-2">{note.text}</p>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[7px] sm:text-[9px] font-medium px-1 sm:px-2 py-0.5 rounded-full ${
                        note.status === 'open' ? 'bg-cream text-ink-secondary' :
                        note.status === 'in_progress' ? 'bg-amber-50 text-amber-600' :
                        'bg-emerald-50 text-emerald-600'
                      }`}>
                        {note.status === 'open' ? 'Open' : note.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                      </span>
                    </div>
                  </div>
                  {/* Threaded reply */}
                  {note.reply && (
                    <div className="px-1.5 sm:px-3 pb-1.5 sm:pb-3 pt-0">
                      <div className="border-t border-ink-faint/20 pt-1.5 sm:pt-2">
                        <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                          {note.reply.avatar ? (
                            <img src={note.reply.avatar} alt={note.reply.author} className="w-3 h-3 sm:w-4 sm:h-4 rounded-full object-cover" />
                          ) : (
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-rose-500 flex items-center justify-center text-[5px] sm:text-[7px] font-bold text-white">
                              {note.reply.initials}
                            </div>
                          )}
                          <span className="text-[8px] sm:text-[10px] font-medium text-ink">{note.reply.author}</span>
                        </div>
                        <p className="text-[8px] sm:text-[10px] text-ink-muted leading-relaxed">{note.reply.text}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Cayda pill */}
        <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 flex items-center gap-1 sm:gap-1.5 bg-accent text-white rounded-full px-2 py-1.5 sm:px-3 sm:py-2 text-[9px] sm:text-[11px] font-medium shadow-lg shadow-accent/20 z-10 pointer-events-auto">
          <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Note
          <span className="w-px h-2.5 sm:h-3 bg-white/25 mx-0.5" />
          <span className="text-white/70 text-[8px] sm:text-[10px] tabular-nums">3</span>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   BEFORE/AFTER — Messy collage vs Cayda
   ═══════════════════════════════════════════ */

const FeedbackCollage = () => (
  <div className="relative w-full" style={{ minHeight: 440 }}>
    {/* Scattered feedback artifacts on a messy desk */}

    {/* 1 — Screenshot with crude red markup */}
    <div className="absolute" style={{ left: '2%', top: '8%', transform: 'rotate(-3deg)', zIndex: 3 }}>
      <div className="w-56 sm:w-72 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          <img src="/demos/glossier.webp" alt="" className="w-full h-28 sm:h-36 object-cover object-top" />
          {/* Red circle drawn in Preview */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 288 144" fill="none">
            <ellipse cx="180" cy="55" rx="52" ry="30" stroke="#FF0000" strokeWidth="2.5" strokeDasharray="0" fill="none" style={{ filter: 'url(#roughen)' }} />
            <line x1="235" y1="40" x2="268" y2="18" stroke="#FF0000" strokeWidth="2" />
            <defs>
              <filter id="roughen">
                <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="noise" />
                <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
              </filter>
            </defs>
          </svg>
          {/* Hand-written "FIX THIS" */}
          <span className="absolute top-2 right-2 text-[11px] font-bold text-red-500" style={{ transform: 'rotate(2deg)' }}>FIX THIS ↑</span>
        </div>
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
          <p className="text-[10px] text-gray-400 font-mono truncate">Screenshot 2024-11-14 at 3.42.17 PM.png</p>
        </div>
      </div>
    </div>

    {/* 2 — Slack message thread */}
    <div className="absolute" style={{ right: '3%', top: '2%', transform: 'rotate(2deg)', zIndex: 5 }}>
      <div className="w-52 sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#4A154B] text-white">
          <img src="/integrations/slack.png" alt="Slack" className="w-3.5 h-3.5 object-contain" />
          <span className="text-[10px] font-semibold">#design-feedback</span>
        </div>
        <div className="px-3 py-2 space-y-2.5">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded bg-rose-100 flex items-center justify-center text-[8px] font-bold text-rose-600 flex-shrink-0">SK</div>
            <div>
              <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold text-gray-900">Sarah K.</span><span className="text-[9px] text-gray-400">3:42 PM</span></div>
              <p className="text-[10px] text-gray-600 leading-relaxed">The hero section looks off, see screenshot above ↑</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded bg-sky-100 flex items-center justify-center text-[8px] font-bold text-sky-600 flex-shrink-0">HG</div>
            <div>
              <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold text-gray-900">Holly G.</span><span className="text-[9px] text-gray-400">3:44 PM</span></div>
              <p className="text-[10px] text-gray-600 leading-relaxed">Which page? Production or staging?</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded bg-rose-100 flex items-center justify-center text-[8px] font-bold text-rose-600 flex-shrink-0">SK</div>
            <div>
              <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold text-gray-900">Sarah K.</span><span className="text-[9px] text-gray-400">3:45 PM</span></div>
              <p className="text-[10px] text-gray-600 leading-relaxed">Production. The /shop page I think?</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded bg-emerald-100 flex items-center justify-center text-[8px] font-bold text-emerald-600 flex-shrink-0">MR</div>
            <div>
              <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold text-gray-900">Mike R.</span><span className="text-[9px] text-gray-400">3:51 PM</span></div>
              <p className="text-[10px] text-gray-600 leading-relaxed">Can you circle the specific part?</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* 3 — Email chain */}
    <div className="absolute" style={{ left: '8%', top: '55%', transform: 'rotate(1.5deg)', zIndex: 2 }}>
      <div className="w-52 sm:w-60 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-1.5 mb-1">
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
            <span className="text-[9px] text-gray-400">Mail</span>
          </div>
          <p className="text-[10px] font-semibold text-gray-800 leading-tight">Re: Re: Re: Homepage feedback</p>
          <p className="text-[9px] text-gray-400 mt-0.5">Sarah K., Holly G., Mike R., +2</p>
        </div>
        <div className="px-3 py-2">
          <p className="text-[10px] text-gray-500 leading-relaxed">Sorry, I was referring to the badge on the product card, not the nav. See my previous email from Tuesday with the Figma link…</p>
          <p className="text-[9px] text-gray-300 mt-1.5">Show 14 more messages</p>
        </div>
      </div>
    </div>

    {/* 4 — Figma comment */}
    <div className="absolute" style={{ left: '42%', top: '42%', transform: 'rotate(-1deg)', zIndex: 4 }}>
      <div className="w-44 sm:w-52 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E1E1E]">
          <img src="/integrations/figma.png" alt="Figma" className="w-3 h-3 object-contain" />
          <span className="text-[9px] text-gray-400 font-medium">Homepage — v3 Final FINAL</span>
        </div>
        <div className="px-3 py-2">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-4 h-4 rounded-full bg-sky-500 flex items-center justify-center text-[7px] font-bold text-white">HG</div>
            <span className="text-[9px] font-semibold text-gray-800">Holly G.</span>
            <span className="text-[8px] text-gray-400">Nov 12</span>
          </div>
          <p className="text-[10px] text-gray-600 leading-relaxed">This doesn’t match what’s on production. Which version is live?</p>
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-[8px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-medium">🤔 3</span>
            <span className="text-[8px] text-gray-300">4 replies</span>
          </div>
        </div>
      </div>
    </div>

    {/* 5 — Loom video thumbnail */}
    <div className="absolute hidden sm:block" style={{ right: '5%', top: '56%', transform: 'rotate(-2deg)', zIndex: 3 }}>
      <div className="w-48 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="relative bg-gray-900 h-24 flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <svg className="w-4 h-4 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </div>
          <div className="absolute bottom-1.5 right-2 text-[9px] text-white/70 font-mono">4:32</div>
          <div className="absolute top-2 left-2 flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#625DF5]" />
            <span className="text-[8px] text-white/60 font-medium">Loom</span>
          </div>
        </div>
        <div className="px-2.5 py-2">
          <p className="text-[10px] font-medium text-gray-800 truncate">Homepage walkthrough v3.mp4</p>
          <p className="text-[9px] text-gray-400">Sarah K. · Nov 14</p>
        </div>
      </div>
    </div>

    {/* 6 — Jira ticket */}
    <div className="absolute hidden sm:block" style={{ left: '30%', top: '78%', transform: 'rotate(2.5deg)', zIndex: 2 }}>
      <div className="w-52 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#0052CC] text-white">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 00-.84-.84h-9.63zm-4.67 4.65c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V7.49a.84.84 0 00-.84-.84H6.86zM2 11.3c.01 2.4 1.97 4.35 4.35 4.35h1.78v1.7C8.13 19.75 10.07 21.69 12 21.7V12.14a.84.84 0 00-.84-.84H2z" /></svg>
          <span className="text-[9px] font-semibold">DESIGN-247</span>
        </div>
        <div className="px-3 py-2">
          <p className="text-[10px] font-medium text-gray-800">Hero section — layout broken</p>
          <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">See attached screenshot from Sarah. Not sure which element she means…</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[8px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">To Do</span>
            <span className="text-[8px] text-gray-300">Unassigned</span>
          </div>
        </div>
      </div>
    </div>

    {/* 7 — Notion doc snippet */}
    <div className="absolute" style={{ left: '32%', top: '1%', transform: 'rotate(1.5deg)', zIndex: 1 }}>
      <div className="w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
          <span className="text-[13px]">📋</span>
          <span className="text-[9px] font-medium text-gray-500">QA Feedback Log</span>
        </div>
        <div className="px-3 py-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <input type="checkbox" className="w-3 h-3 rounded border-gray-300 pointer-events-none" readOnly />
            <span className="text-[10px] text-gray-700">Header overlaps on mobile</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" className="w-3 h-3 rounded border-gray-300 pointer-events-none" defaultChecked readOnly />
            <span className="text-[10px] text-gray-400 line-through">Fix CTA color</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" className="w-3 h-3 rounded border-gray-300 pointer-events-none" readOnly />
            <span className="text-[10px] text-gray-700">Badge positioning (see Slack)</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" className="w-3 h-3 rounded border-gray-300 pointer-events-none" readOnly />
            <span className="text-[10px] text-gray-700">Price format — ask brand team</span>
          </div>
          <p className="text-[8px] text-gray-300 mt-1">Last edited by Sarah K. · Nov 14</p>
        </div>
      </div>
    </div>

    {/* 8 — iMessage / text thread */}
    <div className="absolute" style={{ right: '1%', top: '38%', transform: 'rotate(-1.5deg)', zIndex: 4 }}>
      <div className="w-44 sm:w-52 bg-gray-100 rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-1.5 text-center border-b border-gray-200/60">
          <span className="text-[9px] font-semibold text-gray-500">iMessage</span>
        </div>
        <div className="px-2.5 py-2 space-y-1.5">
          <div className="flex justify-end"><div className="bg-[#007AFF] text-white text-[10px] leading-relaxed px-2.5 py-1.5 rounded-2xl rounded-tr-sm max-w-[85%]">hey did you see the homepage? something&apos;s broken</div></div>
          <div className="flex justify-start"><div className="bg-[#E9E9EB] text-gray-800 text-[10px] leading-relaxed px-2.5 py-1.5 rounded-2xl rounded-tl-sm max-w-[85%]">Which part? Send me a screenshot</div></div>
          <div className="flex justify-end"><div className="bg-[#007AFF] text-white text-[10px] leading-relaxed px-2.5 py-1.5 rounded-2xl rounded-tr-sm max-w-[85%]">hold on let me find it again…</div></div>
        </div>
      </div>
    </div>

    {/* 9 — Google Doc comment */}
    <div className="absolute hidden sm:block" style={{ left: '62%', top: '72%', transform: 'rotate(-2.5deg)', zIndex: 3 }}>
      <div className="w-52 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F8F9FA] border-b border-gray-100">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" fill="#4285F4" /><path d="M14 2v6h6" fill="#A1C2FA" /><path d="M8 13h8v1.5H8zm0 3h6v1.5H8z" fill="white" /></svg>
          <span className="text-[9px] font-medium text-gray-500 truncate">Homepage Copy — Q4 Refresh</span>
        </div>
        <div className="px-3 py-2">
          <div className="border-l-2 border-[#FBBC04] pl-2 mb-1.5">
            <p className="text-[10px] text-gray-400 italic">“…up to 50% off select bestsellers…”</p>
          </div>
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-[7px] font-bold text-white">MR</div>
            <span className="text-[9px] font-semibold text-gray-800">Mike R.</span>
            <span className="text-[8px] text-gray-400">Nov 13</span>
          </div>
          <p className="text-[10px] text-gray-600 leading-relaxed">Is this live or still in review? I see different copy on production.</p>
          <span className="text-[8px] text-blue-500 mt-1 block">Resolve</span>
        </div>
      </div>
    </div>

    {/* 10 — Sticky note / Apple Note */}
    <div className="absolute" style={{ left: '0%', top: '36%', transform: 'rotate(-4deg)', zIndex: 1 }}>
      <div className="w-36 sm:w-40 bg-[#FFF9C4] rounded shadow-lg border border-[#F0E68C]/60 p-3">
        <p className="text-[11px] text-yellow-900/80 leading-relaxed font-mono">
          - fix hero on mobile<br />
          - ask Holly re: badge<br />
          - update price format<br />
          - check w/ Sarah
        </p>
        <p className="text-[8px] text-yellow-700/40 mt-2 text-right">Notes app</p>
      </div>
    </div>
  </div>
);

const BeforeAfterDemo = () => {
  const [ref, inView] = useInView();
  const [mode, setMode] = useState('before'); // 'before' | 'after'
  const autoSwitched = useRef(false);

  useEffect(() => {
    if (!inView || autoSwitched.current) return;
    const t = setTimeout(() => {
      autoSwitched.current = true;
      setMode('after');
    }, 7000);
    return () => clearTimeout(t);
  }, [inView]);

  const isBefore = mode === 'before';

  return (
    <div ref={ref}>
      {/* Toggle */}
      <div className="flex items-center justify-center mb-8">
        <div className="inline-flex items-center bg-white border border-ink-faint/30 rounded-full p-1 shadow-soft">
          <button
            onClick={() => setMode('before')}
            className={`px-4 sm:px-5 py-2 text-xs sm:text-sm font-display font-medium rounded-full transition-all duration-300 ${
              isBefore ? 'bg-red-500/10 text-red-600' : 'text-ink-muted hover:text-ink-secondary'
            }`}
          >
            <span className="hidden sm:inline">Without Cayda</span>
            <span className="sm:hidden">Before</span>
          </button>
          <button
            onClick={() => setMode('after')}
            className={`px-4 sm:px-5 py-2 text-xs sm:text-sm font-display font-medium rounded-full transition-all duration-300 ${
              !isBefore ? 'bg-accent/10 text-accent' : 'text-ink-muted hover:text-ink-secondary'
            }`}
          >
            <span className="hidden sm:inline">With Cayda</span>
            <span className="sm:hidden">After</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative" style={{ minHeight: 420 }}>
        {/* Before — messy collage */}
        <div
          className="w-full"
          style={{
            opacity: isBefore ? 1 : 0,
            transform: isBefore ? 'scale(1)' : 'scale(0.97)',
            transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
            pointerEvents: isBefore ? 'auto' : 'none',
            position: isBefore ? 'relative' : 'absolute',
            top: 0,
            left: 0,
            right: 0,
          }}
        >
          <FeedbackCollage />
        </div>

        {/* After — Cayda screenshot demo */}
        <div
          className="w-full"
          style={{
            opacity: !isBefore ? 1 : 0,
            transform: !isBefore ? 'scale(1)' : 'scale(0.97)',
            transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
            pointerEvents: !isBefore ? 'auto' : 'none',
            position: !isBefore ? 'relative' : 'absolute',
            top: 0,
            left: 0,
            right: 0,
          }}
        >
          <ScreenshotDemo />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SCREENSHOT DEMO — Glossier (interactive)
   ═══════════════════════════════════════════ */

const GLOSSIER_NOTES = [
  { id: 1, x: 18, y: 52, author: 'Sarah K.', initials: 'SK', color: 'bg-rose-500', time: '3m ago', text: 'use the image of her holding the 100ml instead \u2014 make sure logo is front facing.', reply: null, status: 'open' },
  { id: 2, x: 62, y: 40, author: 'Holly G.', initials: 'HG', color: 'bg-sky-500', avatar: '/demos/holly.webp', time: '11m ago', text: 'Bestseller badge overlaps the model\u2019s face on tablet. Needs a position tweak.', reply: { author: 'Sarah K.', initials: 'SK', text: 'Yep, same on the 768px breakpoint. I\u2019ll file it.' }, status: 'in_progress' },
  { id: 3, x: 38, y: 82, author: 'Mike R.', initials: 'MR', color: 'bg-emerald-500', time: '24m ago', text: 'Let\u2019s only show alternative pic on hover over, for mobile do nothing (already confirmed with brand).', reply: null, status: 'resolved' },
];

const ScreenshotDemo = () => {
  const [ref, inView] = useInView();
  const [phase, setPhase] = useState(0);
  const [activeNote, setActiveNote] = useState(null);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    if (!inView) return;
    const t = [];
    t.push(setTimeout(() => setPhase(1), 400));
    t.push(setTimeout(() => setPhase(2), 800));
    t.push(setTimeout(() => setPhase(3), 1200));
    t.push(setTimeout(() => setActiveNote(1), 1800));
    return () => t.forEach(clearTimeout);
  }, [inView]);

  useEffect(() => {
    if (hovering || phase < 3) return;
    const i = setInterval(() => setActiveNote(p => (!p || p >= 3) ? 1 : p + 1), 4000);
    return () => clearInterval(i);
  }, [hovering, phase]);

  return (
    <div
      ref={ref}
      className="relative select-none"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); setActiveNote(null); }}
    >
      {/* Browser frame — rounded corners clipped on chrome only */}
      <div className="rounded-xl sm:rounded-2xl border border-ink-faint/30 shadow-elevated bg-white overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 border-b border-ink-faint/20 bg-[#FAFAF9]">
          <div className="flex gap-1 sm:gap-1.5">
            <div className="w-2 h-2 sm:w-[10px] sm:h-[10px] rounded-full bg-[#FF5F57]" />
            <div className="w-2 h-2 sm:w-[10px] sm:h-[10px] rounded-full bg-[#FEBC2E]" />
            <div className="w-2 h-2 sm:w-[10px] sm:h-[10px] rounded-full bg-[#28C840]" />
          </div>
          <div className="flex-1 mx-3 sm:mx-8">
            <div className="bg-white border border-ink-faint/30 rounded-md sm:rounded-lg px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-[11px] text-ink-muted font-mono max-w-[160px] sm:max-w-[220px] mx-auto text-center flex items-center justify-center gap-1 sm:gap-1.5">
              <svg className="w-2.5 h-2.5 text-emerald-500" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a8 8 0 110 16A8 8 0 018 0zm3.28 5.22a.75.75 0 00-1.06 0L7 8.44 5.78 7.22a.75.75 0 00-1.06 1.06l1.75 1.75a.75.75 0 001.06 0l3.75-3.75a.75.75 0 000-1.06z"/></svg>
              glossier.com/shop
            </div>
          </div>
        </div>

        {/* Screenshot — clipped to browser frame */}
        <div className="relative">
          <img
            src="/demos/glossier.webp"
            alt="Glossier website with Cayda annotations"
            className="w-full block"
            style={{ maxHeight: 420, objectFit: 'cover', objectPosition: 'top' }}
          />
          {/* Subtle bottom fade so the crop looks intentional */}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
        </div>
      </div>

      {/* Annotation layer — OUTSIDE overflow-hidden so cards can break out */}
      <div className="absolute inset-0 pointer-events-none" style={{ top: 37 }}>
        {GLOSSIER_NOTES.slice(0, phase).map((note, i) => {
          const active = activeNote === note.id;
          const isResolved = note.status === 'resolved';
          return (
            <div
              key={note.id}
              className="absolute pointer-events-auto"
              style={{
                left: `${note.x}%`,
                top: `${note.y}%`,
                zIndex: active ? 40 : 20,
                animation: `pinIn 0.5s cubic-bezier(0.34,1.56,0.64,1) ${i * 150}ms backwards`,
              }}
            >
              <button
                onClick={() => setActiveNote(active ? null : note.id)}
                onMouseEnter={() => { setHovering(true); setActiveNote(note.id); }}
                className="relative group"
              >
                <div className={`
                  w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center
                  text-[9px] sm:text-[11px] font-bold text-white
                  ${isResolved ? 'bg-emerald-500 shadow-lg shadow-emerald-500/25' : 'bg-accent shadow-lg shadow-accent/25'}
                  border-2 sm:border-[2.5px] border-white
                  cursor-pointer transition-all duration-200
                  ${active ? 'scale-110' : 'group-hover:scale-110'}
                `}>
                  {isResolved ? (
                    <svg className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : note.id}
                </div>
                {note.status === 'open' && (
                  <div className="absolute inset-0 rounded-full bg-accent animate-ripple" />
                )}
              </button>

              {/* Comment card */}
              {active && (
                <div
                  className={`absolute ${note.x > 50 ? 'right-6 sm:right-9 left-auto' : 'left-6 sm:left-9'} -top-1 sm:-top-2 w-36 sm:w-60 bg-white/95 backdrop-blur-xl border border-ink-faint/30 rounded-lg sm:rounded-xl shadow-elevated z-40`}
                  style={{ animation: 'scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)' }}
                >
                  <div className="p-1.5 sm:p-3">
                    <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                      {note.avatar ? (
                        <img src={note.avatar} alt={note.author} className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full object-cover" />
                      ) : (
                        <div className={`w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-full ${note.color} flex items-center justify-center text-[6px] sm:text-[8px] font-bold text-white`}>
                          {note.initials}
                        </div>
                      )}
                      <span className="text-[9px] sm:text-xs font-semibold text-ink">{note.author}</span>
                      <span className="text-[7px] sm:text-[10px] text-ink-muted ml-auto">{note.time}</span>
                    </div>
                    <p className="text-[9px] sm:text-[11px] text-ink-secondary leading-relaxed mb-1 sm:mb-2">{note.text}</p>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[7px] sm:text-[9px] font-medium px-1 sm:px-2 py-0.5 rounded-full ${
                        note.status === 'open' ? 'bg-cream text-ink-secondary' :
                        note.status === 'in_progress' ? 'bg-amber-50 text-amber-600' :
                        'bg-emerald-50 text-emerald-600'
                      }`}>
                        {note.status === 'open' ? 'Open' : note.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                      </span>
                    </div>
                  </div>
                  {/* Threaded reply */}
                  {note.reply && (
                    <div className="px-1.5 sm:px-3 pb-1.5 sm:pb-3 pt-0">
                      <div className="border-t border-ink-faint/20 pt-1.5 sm:pt-2.5">
                        <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                          {note.reply.avatar ? (
                            <img src={note.reply.avatar} alt={note.reply.author} className="w-3 h-3 sm:w-4 sm:h-4 rounded-full object-cover" />
                          ) : (
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-rose-500 flex items-center justify-center text-[5px] sm:text-[7px] font-bold text-white">
                              {note.reply.initials}
                            </div>
                          )}
                          <span className="text-[8px] sm:text-[10px] font-medium text-ink">{note.reply.author}</span>
                        </div>
                        <p className="text-[8px] sm:text-[10px] text-ink-muted leading-relaxed">{note.reply.text}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Cayda pill */}
        <div className="absolute bottom-2 right-2 sm:bottom-5 sm:right-5 flex items-center gap-1 sm:gap-1.5 bg-accent text-white rounded-full px-2 py-1.5 sm:px-3 sm:py-2 text-[9px] sm:text-[11px] font-medium shadow-lg shadow-accent/20 z-10 pointer-events-auto">
          <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Note
          <span className="w-px h-2.5 sm:h-3 bg-white/25 mx-0.5" />
          <span className="text-white/70 text-[8px] sm:text-[10px] tabular-nums">3</span>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   INTEGRATION REQUEST CARD
   ═══════════════════════════════════════════ */

const IntegrationRequestCard = () => {
  const [value, setValue] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | success

  const showEmail = value.trim().length > 0;

  const handleSubmit = async () => {
    if (!value.trim()) return;
    setState('loading');
    try {
      await fetch(`${API_URL}/api/integration-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integration: value.trim(),
          email: email.trim() || undefined,
        }),
      });
    } catch (e) { void e; /* fire and forget */ }
    setState('success');
  };

  if (state === 'success') {
    return (
      <div className="flex items-start gap-4 bg-on-dark-faint/40 border border-on-dark-faint/30 rounded-xl p-5">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
        <div>
          <h3 className="text-sm font-display font-semibold text-on-dark mb-1">Request received!</h3>
          <p className="text-[13px] font-display text-on-dark-muted leading-relaxed">We&apos;ll look into adding this integration. Thanks for the suggestion.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4 bg-on-dark-faint/40 border border-dashed border-on-dark-faint/50 rounded-xl p-5">
      <div className="w-10 h-10 rounded-xl bg-on-dark-faint/60 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg className="w-5 h-5 text-on-dark-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-display font-semibold text-on-dark mb-1">Request an integration</h3>
        <p className="text-[13px] font-display text-on-dark-muted leading-relaxed mb-3">Need a tool we don&apos;t support yet? Let us know.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. Linear, Notion, Trello…"
            className="flex-1 min-w-0 px-3 py-2 text-sm font-display bg-on-dark-faint/50 border border-on-dark-faint/40 rounded-lg text-on-dark placeholder:text-on-dark-muted/50 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/20 transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            disabled={state === 'loading'}
          />
          <button
            onClick={handleSubmit}
            disabled={!value.trim() || state === 'loading'}
            className="px-4 py-2 text-sm font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            {state === 'loading' ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin block" />
            ) : 'Submit'}
          </button>
        </div>
        <div
          className="overflow-hidden"
          style={{
            maxHeight: showEmail ? 60 : 0,
            opacity: showEmail ? 1 : 0,
            transition: 'max-height 0.3s cubic-bezier(0.16,1,0.3,1), opacity 0.3s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email (optional) — we'll notify you"
            className="w-full mt-2 px-3 py-2 text-sm font-display bg-on-dark-faint/50 border border-on-dark-faint/40 rounded-lg text-on-dark placeholder:text-on-dark-muted/50 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/20 transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            disabled={state === 'loading'}
          />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   FOOTER NEWSLETTER CAPTURE (dark bg)
   ═══════════════════════════════════════════ */

const FooterNewsletterCapture = () => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setState('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'landing_footer' }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong');
      }

      setState('success');
    } catch (err) {
      setState('error');
      setErrorMsg(err.message || 'Something went wrong. Try again.');
    }
  };

  if (state === 'success') {
    return (
      <Reveal delay={60}>
        <div className="flex items-center justify-center gap-2 mt-6">
          <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
          <p className="text-sm font-display text-on-dark">You&apos;re on the list. We&apos;ll keep you posted.</p>
        </div>
      </Reveal>
    );
  }

  return (
    <>
      <Reveal delay={60}>
        <p className="text-base text-on-dark-muted font-display mb-6">
          Product updates and launch news. No spam.
        </p>
      </Reveal>
      <Reveal delay={120}>
        <div className="flex gap-2 max-w-sm mx-auto">
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
            placeholder="you@company.com"
            className="flex-1 px-4 py-2.5 text-sm font-display bg-white/10 border border-on-dark-faint/30 rounded-full text-on-dark placeholder:text-on-dark-muted/50 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 transition-colors"
            disabled={state === 'loading'}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
          />
          <button
            onClick={handleSubmit}
            disabled={state === 'loading' || !email.trim()}
            className="px-5 py-2.5 text-sm font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {state === 'loading' ? (
              <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
            ) : 'Subscribe'}
          </button>
        </div>
        {state === 'error' && (
          <p className="text-xs font-display text-red-400 mt-2">{errorMsg}</p>
        )}
      </Reveal>
    </>
  );
};

/* ═══════════════════════════════════════════
   ICONS (inline SVGs)
   ═══════════════════════════════════════════ */

const PinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a4 4 0 0 0-4 4c0 3 4 7 4 7s4-4 4-7a4 4 0 0 0-4-4z" />
    <circle cx="12" cy="6" r="1" fill="currentColor" />
    <line x1="12" y1="13" x2="12" y2="22" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const MonitorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const TagIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const ZapIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const CodeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

/* ═══════════════════════════════════════════
   LANDING PAGE
   ═══════════════════════════════════════════ */

const LandingPage = ({ isSignedIn }) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [annual, setAnnual] = useState(true);
  const [navScrolled, setNavScrolled] = useState(false);
  const [dbName, setDbName] = useState(null);
  const [dbAvatar, setDbAvatar] = useState(null);
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  const fetchProfileName = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${API_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      const p = data.profile;
      setDbName(p.display_name || p.first_name || null);
      setDbAvatar(p.avatar_url || user?.imageUrl || null);
    } catch (_) { setDbAvatar(user?.imageUrl || null); }
    finally { setAvatarLoaded(true); }
  }, [getToken, user?.imageUrl]);

  useEffect(() => { if (user?.id) fetchProfileName(); }, [user?.id, fetchProfileName]);

  // SEOHead is rendered in JSX below

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auth-aware helpers
  const userEmail = user?.emailAddresses?.[0]?.emailAddress;
  const rawFirst = dbName?.split(' ')[0] || user?.firstName || userEmail?.split('@')[0] || '';
  const userName = rawFirst ? rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1).toLowerCase() : null;
  const userInitial = (userName || userEmail || '?')[0].toUpperCase();
  const greeting = (() => {
    const h = new Date().getHours();
    return h < 5 ? 'Hello' : h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : h < 23 ? 'Good evening' : 'Hello';
  })();

  return (
    <>
    <SEOHead path="/" ogTitle="Cayda — Stop screenshotting your own website." jsonLd={[softwareAppSchema(), organizationSchema(), faqSchema()]} />
    <Helmet><link rel="preload" as="image" href="/demos/vercel.webp" fetchpriority="high" /></Helmet>
    <div className="min-h-screen bg-cream">

      {/* ── FLOATING NAV ── */}
      <header className="fixed top-0 inset-x-0 z-50 pt-4 px-4 sm:px-6">
        {isSignedIn ? (
          /* ── Signed-in: three-column layout ── */
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            {/* Left — logo */}
            <Link to="/" className="text-[15px] font-display font-bold text-ink tracking-tight">
              cayda
            </Link>

            {/* Center — content nav pill (hidden on mobile) */}
            <nav className={`
              hidden md:flex items-center gap-1 px-2 py-1.5 rounded-full
              transition-all duration-300 ease-cayda
              ${navScrolled
                ? 'bg-white/80 backdrop-blur-xl shadow-soft border border-ink-faint/30'
                : 'bg-white/50 backdrop-blur-lg border border-transparent'
              }
            `}>
              <a href="#product" className="px-3 py-1.5 text-sm font-display font-medium text-ink-secondary hover:text-ink transition-colors">
                Product
              </a>
              <a href="#integrations" className="px-3 py-1.5 text-sm font-display font-medium text-ink-secondary hover:text-ink transition-colors">
                Integrations
              </a>
              <a href="#pricing" className="px-3 py-1.5 text-sm font-display font-medium text-ink-secondary hover:text-ink transition-colors">
                Pricing
              </a>
            </nav>

            {/* Right — dashboard + avatar */}
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard"
                className="px-4 py-1.5 text-sm font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-full transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/settings"
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-display font-semibold transition-all overflow-hidden
                  ${navScrolled
                    ? 'bg-white/80 backdrop-blur-xl border border-ink-faint/30 text-accent shadow-soft'
                    : 'bg-white/50 backdrop-blur-lg border border-transparent text-accent'
                  }
                  hover:bg-white
                `}
              >
                {!avatarLoaded ? null : dbAvatar
                  ? <img src={dbAvatar} alt="" className="w-full h-full object-cover" />
                  : userInitial
                }
              </Link>
            </div>
          </div>
        ) : (
          /* ── Signed-out: centered pill ── */
          <div className="flex justify-center">
            <nav className={`
              flex items-center gap-1 px-2 py-1.5 rounded-full
              transition-all duration-300 ease-cayda
              ${navScrolled
                ? 'bg-white/80 backdrop-blur-xl shadow-soft border border-ink-faint/30'
                : 'bg-white/50 backdrop-blur-lg border border-transparent'
              }
            `}>
              <Link to="/" className="px-3 py-1.5 text-[15px] font-display font-bold text-ink tracking-tight">
                cayda
              </Link>

              <div className="w-px h-4 bg-ink-faint/40 mx-1 hidden sm:block" />

              <a href="#product" className="px-3 py-1.5 text-sm font-display font-medium text-ink-secondary hover:text-ink transition-colors hidden sm:block">
                Product
              </a>
              <a href="#pricing" className="px-3 py-1.5 text-sm font-display font-medium text-ink-secondary hover:text-ink transition-colors hidden sm:block">
                Pricing
              </a>

              <div className="w-px h-4 bg-ink-faint/40 mx-1" />

              <Link to="/sign-in" className="px-3 py-1.5 text-sm font-display font-medium text-ink-secondary hover:text-ink transition-colors">
                Sign in
              </Link>
              <Link to="/sign-up" className="px-4 py-1.5 text-sm font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-full transition-colors">
                Try it free →
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main>

        {/* ── HERO ── */}
        {isSignedIn ? (
          <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-6 relative">
            {/* Subtle dot-grid background */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle, #1A1A1A 0.5px, transparent 0.5px)',
              backgroundSize: '20px 20px',
              maskImage: 'radial-gradient(ellipse 70% 60% at 30% 50%, black 20%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 30% 50%, black 20%, transparent 70%)',
            }} />
            <div className="max-w-6xl mx-auto">
              <div className="max-w-2xl">
                <Reveal>
                  <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] tracking-tight leading-[1.15] mb-4">
                    <span className="font-display font-bold text-ink">{greeting}{userName ? `, ${userName}` : ''}</span>
                  </h1>
                </Reveal>
                <Reveal delay={60}>
                  <p className="text-base sm:text-lg text-ink-secondary leading-relaxed mb-10 font-display">
                    Pick up where you left off.
                  </p>
                </Reveal>
                <Reveal delay={120}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Link
                      to="/dashboard"
                      className="group flex items-center gap-4 bg-white border border-ink-faint/20 hover:border-ink-faint/40 rounded-xl p-5 transition-all hover:shadow-soft"
                    >
                      <div className="w-10 h-10 rounded-xl bg-accent/8 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/12 transition-colors">
                        <Globe className="w-5 h-5 text-accent" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-display font-semibold text-ink">Sites</p>
                        <p className="text-xs font-display text-ink-muted">Manage & install</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-ink-faint group-hover:text-ink-muted transition-colors" />
                    </Link>
                    <Link
                      to="/notes"
                      className="group flex items-center gap-4 bg-white border border-ink-faint/20 hover:border-ink-faint/40 rounded-xl p-5 transition-all hover:shadow-soft"
                    >
                      <div className="w-10 h-10 rounded-xl bg-amber-500/8 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/12 transition-colors">
                        <StickyNote className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-display font-semibold text-ink">Notes</p>
                        <p className="text-xs font-display text-ink-muted">Review & triage</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-ink-faint group-hover:text-ink-muted transition-colors" />
                    </Link>
                    <Link
                      to="/team"
                      className="group flex items-center gap-4 bg-white border border-ink-faint/20 hover:border-ink-faint/40 rounded-xl p-5 transition-all hover:shadow-soft"
                    >
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/8 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/12 transition-colors">
                        <Users className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-display font-semibold text-ink">Team</p>
                        <p className="text-xs font-display text-ink-muted">Invite & collaborate</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-ink-faint group-hover:text-ink-muted transition-colors" />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>
        ) : (
          <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-6 relative">
            {/* Subtle dot-grid background */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle, #1A1A1A 0.5px, transparent 0.5px)',
              backgroundSize: '20px 20px',
              maskImage: 'radial-gradient(ellipse 70% 60% at 30% 50%, black 20%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 30% 50%, black 20%, transparent 70%)',
            }} />
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                {/* Text */}
                <div className="max-w-xl">
                  <Reveal>
                    <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-display font-bold text-ink tracking-tight leading-[1.15] mb-5">
                      The website feedback tool<br />your visitors never see.
                    </h1>
                  </Reveal>
                  <Reveal delay={80}>
                    <p className="text-base sm:text-lg text-ink-secondary leading-relaxed mb-8 max-w-md font-display">
                      Your team pins notes, bugs, and design feedback on live pages. One script tag. Nothing changes for your audience.
                    </p>
                  </Reveal>
                  <Reveal delay={140}>
                    <div className="flex flex-wrap items-center gap-3">
                      <Link to="/sign-up" className="inline-flex items-center px-6 py-3 text-[15px] font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-full transition-all duration-200 hover:shadow-glow">
                        Try it on your site →
                      </Link>
                      <a href="#product" className="inline-flex items-center px-5 py-3 text-[15px] font-display font-medium text-ink-secondary border border-ink-faint/50 hover:border-ink-faint rounded-full transition-colors bg-white/60">
                        See the difference
                      </a>
                    </div>
                  </Reveal>

                </div>

                {/* Demo */}
                <Reveal delay={200} y={24}>
                  <InteractiveDemo />
                </Reveal>
              </div>
            </div>
          </section>
        )}

        {/* ── CORE VALUE PROPOSITION ── */}
        <section id="product" className="py-16 sm:py-24 px-6 bg-cream">
          <div className="max-w-5xl mx-auto">
            <div className="max-w-2xl mb-16">
              <Reveal>
                <p className="text-xs font-mono uppercase tracking-label text-ink-muted mb-4">The invisible layer</p>
              </Reveal>
              <Reveal delay={50}>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-5">
                  <span className="font-serif italic text-ink-secondary">Stop screenshotting</span>{' '}
                  <span className="font-display font-bold text-ink">your own website.</span>
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="text-base sm:text-lg text-ink-secondary leading-relaxed font-display max-w-lg">
                  Pin notes directly on the page. Click, comment, done. Your team sees everything — your visitors never do.
                </p>
              </Reveal>
            </div>

            {/* Before/after toggle demo */}
            <Reveal delay={150} y={32}>
              <BeforeAfterDemo />
            </Reveal>
          </div>
        </section>

        {/* ── HOW IT WORKS (dark) ── */}
        <section id="how-it-works" className="bg-dark py-16 sm:py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-label text-on-dark-muted mb-4">How it works</p>
            </Reveal>
            <Reveal delay={50}>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-on-dark tracking-tight mb-14">
                Three steps. Thirty seconds.
              </h2>
            </Reveal>

            <div className="grid sm:grid-cols-3 gap-6 sm:gap-10 mb-14">
              {[
                {
                  num: '01',
                  title: 'Add your site',
                  desc: 'Paste a URL. Works with React, Next.js, WordPress, Shopify — anything.',
                  visual: (
                    <div className="space-y-3">
                      <div className="bg-on-dark-faint/50 rounded-lg px-4 py-3 border border-on-dark-faint/30">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded bg-on-dark-muted/30" />
                          <span className="text-xs font-mono text-on-dark-muted">https://your-site.com</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <img src="/integrations/wordpress.png" alt="WordPress" className="w-5 h-5 rounded object-contain opacity-40" />
                        <img src="/integrations/shopify.png" alt="Shopify" className="w-5 h-5 rounded object-contain opacity-40" />
                        <span className="text-[10px] font-display text-on-dark-muted/50">+ any stack</span>
                      </div>
                    </div>
                  ),
                },
                {
                  num: '02',
                  title: 'Drop in one script',
                  desc: 'One-click install for WordPress & Shopify. One line of code for everything else.',
                  visual: (
                    <div className="bg-[#1a1a2e] rounded-lg p-4 border border-on-dark-faint/20">
                      <code className="text-[11px] leading-relaxed font-mono">
                        <span className="text-on-dark-muted">&lt;</span>
                        <span className="text-amber-400">script</span>
                        {' '}
                        <span className="text-cyan-400">src</span>
                        <span className="text-on-dark-muted">=</span>
                        <span className="text-emerald-400">"…/widget.js"</span>
                        <br />
                        {'  '}
                        <span className="text-cyan-400">data-token</span>
                        <span className="text-on-dark-muted">=</span>
                        <span className="text-emerald-400">"your-token"</span>
                        <span className="text-on-dark-muted">&gt;&lt;/</span>
                        <span className="text-amber-400">script</span>
                        <span className="text-on-dark-muted">&gt;</span>
                      </code>
                    </div>
                  ),
                },
                {
                  num: '03',
                  title: 'Click to annotate',
                  desc: 'Pin notes. Assign, prioritize, resolve.',
                  visual: (
                    <div className="flex items-center gap-3 bg-on-dark-faint/50 rounded-lg px-4 py-3 border border-on-dark-faint/30">
                      <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-[10px] font-bold text-white shadow-lg shadow-accent/20">
                        1
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-on-dark">Fix header alignment</div>
                        <div className="text-[10px] text-on-dark-muted">Sarah K. · 2m ago</div>
                      </div>
                    </div>
                  ),
                },
              ].map(({ num, title, desc, visual }, i) => (
                <Reveal key={num} delay={i * 80}>
                  <div>
                    <span className="text-5xl sm:text-6xl font-serif text-on-dark-faint/60 block mb-4">{num}</span>
                    <h3 className="text-lg font-display font-bold text-on-dark mb-2">{title}</h3>
                    <p className="text-sm text-on-dark-muted leading-relaxed mb-5 font-display">{desc}</p>
                    {visual}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Stats — proof beat */}
          <Reveal delay={200}>
            <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-mono text-on-dark-muted">
              <span><span className="text-on-dark font-semibold">30s</span> install</span>
              <span className="text-on-dark-faint">·</span>
              <span><span className="text-on-dark font-semibold">Invisible</span> to visitors</span>
              <span className="text-on-dark-faint">·</span>
              <span><span className="text-on-dark font-semibold">Any</span> stack</span>
            </div>
          </Reveal>

          {/* CTA */}
          <Reveal delay={240}>
            <div className="mt-12 text-center">
              <p className="text-sm text-on-dark-muted font-display mb-5">That&apos;s it. Your team can start leaving notes in under a minute.</p>
              <Link to="/sign-up" className="inline-flex items-center px-6 py-3 text-[15px] font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-full transition-all duration-200 hover:shadow-glow">
                Add your first site free →
              </Link>
            </div>
          </Reveal>
        </section>

        {/* ── FEATURES ── */}
        <section className="py-16 sm:py-24 px-6 bg-cream">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-label text-ink-muted mb-4">Features</p>
            </Reveal>
            <Reveal delay={50}>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink tracking-tight mb-12">
                Built for the way teams actually review sites.
              </h2>
            </Reveal>

            {/* Hero features — 3 large cards */}
            <div className="grid sm:grid-cols-3 gap-5 mb-6">
              {/* Invisible to visitors */}
              <Reveal>
                <InvisibleCard
                  icon={<EyeOffIcon />}
                  title="Invisible to visitors"
                  desc="Your customers never see Cayda. Only authenticated team members can. Your live site stays pristine."
                />
              </Reveal>

              {/* Task management dashboard */}
              <Reveal delay={60}>
                <div className="group p-5 rounded-2xl border bg-white border-ink-faint/30 hover:border-ink-faint/60 hover:shadow-soft transition-all duration-200 h-full">
                  <div className="mb-3 text-accent"><TagIcon /></div>
                  <h3 className="text-sm font-display font-bold mb-1.5 text-ink">Task management dashboard</h3>
                  <p className="text-[13px] leading-relaxed font-display text-ink-secondary">
                    Assign, prioritize, set status. Kanban boards, filters, bulk actions — manage feedback like a project, not a thread.
                  </p>
                </div>
              </Reveal>

              {/* Threaded comments */}
              <Reveal delay={120}>
                <div className="group p-5 rounded-2xl border bg-white border-ink-faint/30 hover:border-ink-faint/60 hover:shadow-soft transition-all duration-200 h-full">
                  <div className="mb-3 text-accent"><ChatIcon /></div>
                  <h3 className="text-sm font-display font-bold mb-1.5 text-ink">Threaded comments</h3>
                  <p className="text-[13px] leading-relaxed font-display text-ink-secondary">
                    Reply in context, right on the page. No more &ldquo;which page was that on?&rdquo; Every conversation stays anchored to the element.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Compact feature row */}
            <Reveal delay={160}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { icon: <PinIcon />, title: 'Pin to elements' },
                  { icon: <CameraIcon />, title: 'Auto screenshots' },
                  { icon: <MonitorIcon />, title: 'Environment capture' },
                  { icon: <ZapIcon />, title: 'Slack, Jira & more' },
                  { icon: <CodeIcon />, title: 'Any framework' },
                  { icon: <CameraIcon />, title: 'Screen recording' },
                ].map(({ icon, title }) => (
                  <div key={title} className="flex items-center gap-2.5 bg-white border border-ink-faint/20 rounded-xl px-3.5 py-3 hover:border-ink-faint/40 transition-colors">
                    <div className="text-accent flex-shrink-0">{icon}</div>
                    <span className="text-xs font-display font-medium text-ink">{title}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── INTEGRATIONS (dark) ── */}
        <section id="integrations" className="bg-dark py-14 sm:py-20 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-on-dark tracking-tight mb-3">
                Flows where your team already works.
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <p className="text-sm text-on-dark-muted font-display mb-10">
                Notes become issues, alerts, and tickets — automatically.
              </p>
            </Reveal>
            <Reveal delay={120}>
              <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto text-left">
                {[
                  { name: 'Slack', logo: '/integrations/slack.png', desc: 'Get notified when notes are created or resolved. Reply from Slack and it syncs back.' },
                  { name: 'Jira', logo: '/integrations/jira.png', desc: 'Turn any note into a Jira issue with one click. Status syncs both ways.' },
                  { name: 'Asana', logo: '/integrations/asana.png', desc: 'Push notes to Asana as tasks. Screenshots and context carry over automatically.' },
                ].map(({ name, logo, desc }) => (
                  <div key={name} className="flex items-start gap-4 bg-on-dark-faint/40 border border-on-dark-faint/30 rounded-xl p-5">
                    <img src={logo} alt={name} className="w-10 h-10 rounded-xl object-contain flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-display font-semibold text-on-dark mb-1">{name}</h3>
                      <p className="text-[13px] font-display text-on-dark-muted leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
                <IntegrationRequestCard />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── PRICING ── */}
        <section id="pricing" className="py-16 sm:py-24 px-6 bg-cream">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
              <div>
                <Reveal>
                  <p className="text-xs font-mono uppercase tracking-label text-ink-muted mb-4">Pricing</p>
                </Reveal>
                <Reveal delay={50}>
                  <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink tracking-tight">
                    Simple plans. Start free.
                  </h2>
                </Reveal>
              </div>
              <Reveal delay={80}>
                <div className="inline-flex items-center bg-white border border-ink-faint/30 rounded-full p-1">
                  <button
                    onClick={() => setAnnual(false)}
                    className={`px-4 py-1.5 text-sm font-display font-medium rounded-full transition-all ${
                      !annual ? 'bg-ink text-white' : 'text-ink-secondary hover:text-ink'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setAnnual(true)}
                    className={`px-4 py-1.5 text-sm font-display font-medium rounded-full transition-all ${
                      annual ? 'bg-ink text-white' : 'text-ink-secondary hover:text-ink'
                    }`}
                  >
                    Annual <span className="text-emerald-400 font-semibold">-25%</span>
                  </button>
                </div>
              </Reveal>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Free */}
              <Reveal>
                <div className="border border-ink-faint/30 rounded-2xl p-6 bg-white h-full flex flex-col">
                  <h3 className="text-lg font-display font-bold text-ink">Free</h3>
                  <p className="text-4xl font-display font-bold text-ink mt-2 mb-6">$0</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {['Team notes on your site', 'Centralized task dashboard', '2 websites', '50 notes per site', '3 team members', '30-day history', 'Browser & device info capture', 'Smart element selection', 'Auto screenshots', 'WordPress plugin', 'Shopify compatible'].map(f => (
                      <li key={f} className="flex items-center gap-2.5 text-sm font-display text-ink-secondary">
                        <svg className="w-4 h-4 text-ink-faint flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                        {f === 'WordPress plugin' ? (
                          <span className="inline-flex items-center gap-1.5">
                            <img src="/integrations/wordpress.png" alt="" className="w-4 h-4 rounded-[3px] object-contain" />
                            {f}
                          </span>
                        ) : f === 'Shopify compatible' ? (
                          <span className="inline-flex items-center gap-1.5">
                            <img src="/integrations/shopify.png" alt="" className="w-4 h-4 rounded-[3px] object-contain" />
                            {f}
                          </span>
                        ) : f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/sign-up" className="block text-center px-5 py-2.5 text-sm font-display font-medium border border-ink-faint/40 hover:border-ink-faint rounded-full transition-colors text-ink-secondary hover:text-ink">
                    Add your first site
                  </Link>
                </div>
              </Reveal>

              {/* Pro — featured */}
              <Reveal delay={60}>
                <div className="relative bg-dark rounded-2xl p-6 h-full flex flex-col shadow-elevated">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-[10px] font-display font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Popular
                  </div>
                  <h3 className="text-lg font-display font-bold text-on-dark">Pro</h3>
                  <div className="mt-2 mb-1">
                    <span className="text-4xl font-display font-bold text-on-dark">${annual ? '9' : '12'}</span>
                    <span className="text-sm font-display text-on-dark-muted">/mo</span>
                  </div>
                  <p className="text-xs font-display text-on-dark-muted mb-1">+ ${annual ? '5' : '6'} per seat</p>
                  <p className="text-xs font-display text-on-dark-muted mb-2">{annual ? 'billed annually' : 'billed monthly'}</p>
                  <p className="text-[11px] font-display text-on-dark-muted/60 mb-6 bg-on-dark-faint/30 rounded-lg px-3 py-1.5 inline-block">You + 2 teammates = {annual ? '$19' : '$24'}/mo</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {['Unlimited websites & notes', 'Unlimited team members (per-user pricing)', 'Full note history', 'Invisible mode', 'Status, priority & assignment', 'Attachments', 'Activity feed', 'Export notes (CSV)', 'Slack notifications'].map(f => (
                      <li key={f} className="flex items-center gap-2.5 text-sm font-display text-on-dark/80">
                        <svg className="w-4 h-4 text-accent flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                        {f === 'Slack notifications' ? (
                          <span className="inline-flex items-center gap-1.5">
                            <img src="/integrations/slack.png" alt="" className="w-4 h-4 rounded-[3px] object-contain" />
                            {f}
                          </span>
                        ) : f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/sign-up" className="block text-center px-5 py-2.5 text-sm font-display font-medium bg-accent hover:bg-accent-hover text-white rounded-full transition-colors">
                    Start with Pro →
                  </Link>
                </div>
              </Reveal>

              {/* Team */}
              <Reveal delay={120}>
                <div className="border border-ink-faint/30 rounded-2xl p-6 bg-white h-full flex flex-col">
                  <h3 className="text-lg font-display font-bold text-ink">Team</h3>
                  <div className="mt-2 mb-1">
                    <span className="text-4xl font-display font-bold text-ink">${annual ? '14' : '19'}</span>
                    <span className="text-sm font-display text-ink-muted">/mo</span>
                  </div>
                  <p className="text-xs font-display text-ink-muted mb-1">+ ${annual ? '6' : '8'} per seat</p>
                  <p className="text-xs font-display text-ink-muted mb-6">{annual ? 'billed annually' : 'billed monthly'}</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {['Everything in Pro', 'Screen recording', 'Guest access — share with clients, no member fee', 'Jira integration', 'Asana integration'].map(f => {
                      const logoMap = { 'Jira integration': '/integrations/jira.png', 'Asana integration': '/integrations/asana.png' };
                      const logo = logoMap[f];
                      return (
                        <li key={f} className="flex items-center gap-2.5 text-sm font-display text-ink-secondary">
                          <svg className="w-4 h-4 text-accent/50 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                          {logo ? (
                            <span className="inline-flex items-center gap-1.5">
                              <img src={logo} alt="" className="w-4 h-4 rounded-[3px] object-contain" />
                              {f}
                            </span>
                          ) : f}
                        </li>
                      );
                    })}
                  </ul>
                  <Link to="/sign-up" className="block text-center px-5 py-2.5 text-sm font-display font-medium border border-ink-faint/40 hover:border-ink-faint rounded-full transition-colors text-ink-secondary hover:text-ink">
                    Start with Team →
                  </Link>
                </div>
              </Reveal>
            </div>


          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className="py-16 sm:py-24 px-6 bg-cream border-t border-ink-faint/15">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-label text-ink-muted mb-4">FAQ</p>
            </Reveal>
            <Reveal delay={50}>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink tracking-tight mb-10">Questions, answered.</h2>
            </Reveal>
            <div className="space-y-3">
              {FAQS.map(({ q, a }, i) => (
                <Reveal key={q} delay={80 + i * 40}>
                  <details className="group bg-white border border-ink-faint/20 rounded-2xl open:border-ink-faint/40 transition-colors">
                    <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 text-[15px] font-display font-semibold text-ink [&::-webkit-details-marker]:hidden">
                      {q}
                      <ChevronDown className="w-4 h-4 text-ink-muted shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="px-5 pb-5 text-sm sm:text-[15px] font-display text-ink-secondary leading-relaxed">{a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA (dark) ── */}
        <section className="bg-dark py-16 sm:py-24 px-6 relative overflow-hidden">
          {/* Subtle dot grid background */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }} />

          <div className="relative max-w-2xl mx-auto text-center">
            <Reveal>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-4">
                <span className="font-serif italic text-on-dark/80">Notes that live</span>
                <br />
                <span className="font-display font-bold text-on-dark">where the work lives.</span>
              </h2>
            </Reveal>
            {isSignedIn ? (
              <>
                <Reveal delay={60}>
                  <p className="text-base text-on-dark-muted font-display mb-8">
                    Your site. Your team&apos;s notes.
                  </p>
                </Reveal>
                <Reveal delay={120}>
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center px-7 py-3.5 text-[15px] font-display font-medium text-white bg-accent hover:bg-accent-hover rounded-full transition-all duration-200 hover:shadow-glow"
                  >
                    Go to dashboard →
                  </Link>
                </Reveal>
              </>
            ) : (
              <FooterNewsletterCapture />
            )}
          </div>
        </section>

      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-dark py-10 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Row 1 */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <Link to="/" className="text-[15px] font-display font-bold text-on-dark tracking-tight">cayda</Link>
            <div className="flex items-center gap-5">
              <a href="#product" className="text-sm font-display text-on-dark-muted hover:text-on-dark transition-colors">Product</a>
              <a href="#pricing" className="text-sm font-display text-on-dark-muted hover:text-on-dark transition-colors">Pricing</a>
              <Link to={isSignedIn ? '/dashboard' : '/sign-in'} className="text-sm font-display text-on-dark-muted hover:text-on-dark transition-colors">
                {isSignedIn ? 'Dashboard' : 'Sign in'}
              </Link>
            </div>
          </div>
          {/* Row 2 — SEO content links */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-4">
            <Link to="/website-feedback-tool" className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">Website Feedback Tool</Link>
            <Link to="/website-annotation-tool" className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">Website Annotation Tool</Link>
            <Link to="/how-it-works" className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">How It Works</Link>
            <Link to="/compatibility" className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">Compatibility</Link>
          </div>
          {/* Row 2a — platform pages */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-3">
            <span className="text-xs font-display text-on-dark-muted/70">Cayda for:</span>
            {PLATFORMS.map(p => (
              <Link key={p.slug} to={`/for/${p.slug}`} className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">{p.name}</Link>
            ))}
          </div>
          {/* Row 2b — comparison pages */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-3">
            <span className="text-xs font-display text-on-dark-muted/70">Compare:</span>
            {COMPARISONS.map(c => (
              <Link key={c.slug} to={`/vs/${c.slug}`} className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">vs {c.name}</Link>
            ))}
          </div>
          {/* Row 3 */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-on-dark-faint/30">
            <span className="text-xs font-display text-on-dark-muted">&copy; {new Date().getFullYear()} Slowfast LLC</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">Privacy</Link>
              <Link to="/terms" className="text-xs font-display text-on-dark-muted hover:text-on-dark transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Demo layer for signed-out visitors — temporarily hidden */}
      {/* {!isSignedIn && <DemoLayer />} */}
    </div>
    </>
  );
};

export default LandingPage;
